import React, { useState } from "react";
import { Mail, Phone, MapPin, MessageCircle, Send, Loader2, Check } from "lucide-react";
import { api, formatError } from "@/lib/api";
import { BRAND, waLink } from "@/config/site.config";

export default function ContactSection() {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [demo, setDemo] = useState({ name: "", email: "", company: "", phone: "", product: "AI Queue Pro", message: "" });
  const [state, setState] = useState({ contact: "idle", demo: "idle", err: "" });

  const onContact = async (e) => {
    e.preventDefault();
    setState((s) => ({ ...s, contact: "loading", err: "" }));
    try {
      await api.post("/contact", form);
      setState((s) => ({ ...s, contact: "ok" }));
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      setState((s) => ({ ...s, contact: "err", err: formatError(err.response?.data?.detail) || err.message }));
    }
  };

  const onDemo = async (e) => {
    e.preventDefault();
    setState((s) => ({ ...s, demo: "loading", err: "" }));
    try {
      await api.post("/demo", demo);
      setState((s) => ({ ...s, demo: "ok" }));
      setDemo({ name: "", email: "", company: "", phone: "", product: "AI Queue Pro", message: "" });
    } catch (err) {
      setState((s) => ({ ...s, demo: "err", err: formatError(err.response?.data?.detail) || err.message }));
    }
  };

  const contactLinks = [
    { Icon: MessageCircle, t: "WhatsApp", v: BRAND.phoneDisplay, href: waLink() },
    { Icon: Mail, t: "Email", v: BRAND.email, href: `mailto:${BRAND.email}` },
    { Icon: Phone, t: "Phone", v: BRAND.phoneDisplay, href: `tel:${BRAND.phoneTel}` },
    { Icon: MapPin, t: "Headquarters", v: BRAND.hqDisplay, href: "#map" },
  ];

  return (
    <section id="contact" className="section" data-testid="contact-section">
      <div className="mx-auto max-w-7xl grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <div className="eyebrow mb-4">Contact</div>
          <h2 className="font-serif text-4xl md:text-5xl text-white tracking-tight leading-[1.05]">
            Talk to <span className="gold-text italic">{BRAND.name}</span>.
          </h2>
          <p className="mt-5 text-white/65">
            Whether you need a queue system installed by next month or a custom AI platform engineered from scratch — we&rsquo;d love to hear from you.
          </p>

          <div className="mt-8 space-y-4">
            {contactLinks.map(({ Icon, t, v, href }, i) => (
              <a
                key={i}
                href={href}
                target={href.startsWith("http") ? "_blank" : undefined}
                rel={href.startsWith("http") ? "noopener noreferrer" : undefined}
                className="flex items-center gap-4 glass-card p-4 group"
                data-testid={`contact-link-${t.toLowerCase()}`}
              >
                <div className="h-10 w-10 rounded-xl flex items-center justify-center border border-gold-500/25 bg-gold-500/[0.06]">
                  <Icon className="h-4 w-4 text-gold-300" strokeWidth={1.5} />
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-[0.2em] text-white/40">{t}</div>
                  <div className="text-sm text-white">{v}</div>
                </div>
              </a>
            ))}
          </div>

          <div id="map" className="mt-8 overflow-hidden rounded-2xl border border-white/8">
            <iframe
              title="AIBoxHub HQ Map"
              src={BRAND.mapEmbed}
              width="100%"
              height="220"
              style={{ border: 0, filter: "grayscale(1) invert(0.92) contrast(0.9)" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        <div className="lg:col-span-7 space-y-6">
          {/* Demo form */}
          <div id="demo" className="glass-card p-8">
            <div className="flex items-center justify-between">
              <div>
                <div className="eyebrow text-gold-300">Request a Demo</div>
                <div className="mt-2 font-serif text-2xl md:text-3xl text-white">See it live in 20 minutes.</div>
              </div>
            </div>
            <form onSubmit={onDemo} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name" value={demo.name} onChange={(v) => setDemo({ ...demo, name: v })} testId="demo-name" required />
              <Field label="Email" type="email" value={demo.email} onChange={(v) => setDemo({ ...demo, email: v })} testId="demo-email" required />
              <Field label="Company" value={demo.company} onChange={(v) => setDemo({ ...demo, company: v })} testId="demo-company" />
              <Field label="Phone" value={demo.phone} onChange={(v) => setDemo({ ...demo, phone: v })} testId="demo-phone" />
              <div className="md:col-span-2">
                <Label>Product</Label>
                <select
                  data-testid="demo-product"
                  className="w-full bg-ink-200 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-gold-500/50"
                  value={demo.product}
                  onChange={(e) => setDemo({ ...demo, product: e.target.value })}
                >
                  {["AI Queue Pro","AI Salon","AI Hospital","AI Clinic","AI CRM","AI Booking","AI Voice Bot","AI Business Suite"].map((p) => (
                    <option key={p}>{p}</option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-2">
                <Label>Message</Label>
                <textarea
                  data-testid="demo-message"
                  rows={3}
                  className="w-full bg-ink-200 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-gold-500/50"
                  placeholder="Tell us about your project…"
                  value={demo.message}
                  onChange={(e) => setDemo({ ...demo, message: e.target.value })}
                />
              </div>
              <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm">
                  {state.demo === "ok" && <span className="text-emerald-300 inline-flex items-center gap-1" data-testid="demo-success"><Check className="h-4 w-4" /> Demo requested.</span>}
                  {state.demo === "err" && <span className="text-red-400">{state.err}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={waLink("Hi%20AIBoxHub%2C%20I%27d%20like%20to%20book%20a%20demo")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pill pill-ghost text-sm"
                    data-testid="demo-whatsapp"
                  >
                    <MessageCircle className="h-4 w-4" /> WhatsApp
                  </a>
                  <button type="submit" disabled={state.demo === "loading"} className="pill pill-gold disabled:opacity-50" data-testid="demo-submit">
                    {state.demo === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Request Demo
                  </button>
                </div>
              </div>
            </form>
          </div>

          {/* Quick contact */}
          <div className="glass-card p-8">
            <div className="eyebrow text-gold-300">Send a Message</div>
            <form onSubmit={onContact} className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field label="Name" value={form.name} onChange={(v) => setForm({ ...form, name: v })} testId="contact-name" required />
              <Field label="Email" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} testId="contact-email" required />
              <div className="md:col-span-2">
                <Field label="Subject" value={form.subject} onChange={(v) => setForm({ ...form, subject: v })} testId="contact-subject" />
              </div>
              <div className="md:col-span-2">
                <Label>Message</Label>
                <textarea
                  data-testid="contact-message"
                  required rows={3}
                  className="w-full bg-ink-200 border border-white/10 rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-gold-500/50"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                />
              </div>
              <div className="md:col-span-2 flex flex-wrap items-center justify-between gap-3">
                <div className="text-sm">
                  {state.contact === "ok" && <span className="text-emerald-300 inline-flex items-center gap-1" data-testid="contact-success"><Check className="h-4 w-4" /> Sent. We&rsquo;ll reply soon.</span>}
                  {state.contact === "err" && <span className="text-red-400">{state.err}</span>}
                </div>
                <div className="flex items-center gap-2">
                  <a
                    href={waLink("Hi%20AIBoxHub%2C%20I%20have%20a%20quick%20question")}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="pill pill-ghost text-sm"
                    data-testid="contact-whatsapp"
                  >
                    <MessageCircle className="h-4 w-4" /> WhatsApp
                  </a>
                  <button type="submit" disabled={state.contact === "loading"} className="pill pill-gold disabled:opacity-50" data-testid="contact-submit">
                    {state.contact === "loading" ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    Send
                  </button>
                </div>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}

function Label({ children }) {
  return <div className="mb-1.5 text-[11px] font-mono uppercase tracking-[0.2em] text-white/50">{children}</div>;
}
function Field({ label, value, onChange, type = "text", required, testId }) {
  return (
    <div>
      <Label>{label}</Label>
      <input
        data-testid={testId}
        type={type}
        required={required}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="w-full bg-ink-200 border border-white/10 rounded-xl px-4 py-3 text-sm text-white placeholder-white/30 outline-none focus:border-gold-500/50"
      />
    </div>
  );
}
